import { useLazyGetAwardeeByIdNameQuery } from "../../services/rtkQueryServices/checkApiService";
import "./Awardees.scss";
import React, { useState, useEffect } from "react";
import UtaProfileLink from "../Shared/UtaProfileLink/UtaProfileLink";
import { useNavigate } from "react-router-dom";
import { Collapse } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

// Legacy: this specific award record is hidden from the awardee's won-awards list.
const HIDDEN_AWARD_ID = 5366;

// Website values are stored bare ("www.informs.org/..."), so a raw href would
// resolve relative to the current route and 404.
const externalHref = (url) =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

// `awards.Amount` is badly named: it is free text describing what the winner
// receives, and only sometimes a sum. Across 437 distinct values it holds money
// ("$1,000.00"), objects ("Medal", "Plaque"), combinations ("Recognition,
// Medal") and occasionally a whole paragraph. The rest of the app surfaces it
// as "Kind", so we do too.
const isRealKind = (a) => a && a !== "Unspecified";

// A handful of rows carry several sentences, which would blow out the narrow
// slider. Show the gist; the full text is on the award page.
const KIND_MAX = 60;
const shortKind = (a) =>
  a.length > KIND_MAX ? `${a.slice(0, KIND_MAX).trimEnd()}…` : a;

const Awardee = ({ awardeeData, sliderTitle, utaUsername, handleOnCloseClick }) => {
  const [setAwardData, { data: awardData }] = useLazyGetAwardeeByIdNameQuery();
  const [currentAwardData, setCurrentAwardData] = useState();
  // Which rows are expanded, keyed by index so duplicate awards don't collide.
  const [openRows, setOpenRows] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (awardData && currentAwardData?.id) {
      // Award detail now lives at /award/:id (the old /award route is gone).
      navigate(`/award/${currentAwardData.id}`, {
        state: { currentAwardData },
      });
      if (handleOnCloseClick) handleOnCloseClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awardData, navigate, currentAwardData]);

  const toggleRow = (index) =>
    setOpenRows((prev) => ({ ...prev, [index]: !prev[index] }));

  // Leaving the page is now an explicit action inside the expanded panel, so
  // that clicking the row itself can expand instead of navigating away.
  const handleViewAward = (data) => {
    if (data.fk_award && data.Award_Name) {
      setAwardData(data.fk_award);
      setCurrentAwardData({
        awardName: data.Award_Name,
        awardkind: data.Amount === "Unspecified" ? "Recognition" : data.Amount,
        category: data.Category,
        level: data.Level,
        awardNotes: data.Notes,
        percentageWomen: data.PercentageWomen,
        firstYearRecorded: data.firstYearRecorded,
        id: data.fk_award,
        website: data.Website,
      });
    }
  };

  const visible = (awardeeData?.data || []).filter(
    (a) => a.fk_award !== HIDDEN_AWARD_ID
  );

  return (
    <>
      <div className="font-weight-bold mb-3 award-title-wrapper">
        Awards won by <UtaProfileLink username={utaUsername}>{sliderTitle}</UtaProfileLink>
      </div>

      {visible.length === 0 && (
        <div className="award-empty">No awards on record.</div>
      )}

      <div>
        {visible.map((award, index) => {
          const isOpen = !!openRows[index];
          // Every field below arrives in the same payload as the award name,
          // so expanding a row costs no additional request.
          const facts = [
            isRealKind(award.Amount) && ["Kind", shortKind(award.Amount)],
            award.Category && ["Category", award.Category],
            award.Level && ["Level", award.Level],
            award.PercentageWomen != null && [
              "Women recipients",
              `${award.PercentageWomen}%`,
            ],
            award.firstYearRecorded && [
              "Tracked since",
              award.firstYearRecorded,
            ],
          ].filter(Boolean);

          return (
            <div
              className="award-wrapper mb-2"
              key={`${award.fk_award}-${index}`}
            >
              <button
                type="button"
                className="award-row"
                aria-expanded={isOpen}
                onClick={() => toggleRow(index)}
              >
                <span className="award-row-main">
                  <span className="award-row-title">
                    {award.Year_of_Award} &middot; {award.Award_Name}
                  </span>
                  {award.Organization && (
                    <span className="award-row-org">{award.Organization}</span>
                  )}
                </span>
                <ExpandMoreRoundedIcon
                  className={`award-row-chevron${isOpen ? " open" : ""}`}
                  fontSize="small"
                />
              </button>

              <Collapse in={isOpen} unmountOnExit>
                <div className="award-detail">
                  <dl className="award-facts">
                    {facts.map(([label, value]) => (
                      <div className="award-fact" key={label}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                      </div>
                    ))}
                  </dl>

                  <div className="award-actions">
                    <button
                      type="button"
                      className="award-link-btn"
                      onClick={() => handleViewAward(award)}
                    >
                      View award page
                    </button>
                    {award.Website && (
                      <a
                        className="award-link-ext"
                        href={externalHref(award.Website)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Official site
                      </a>
                    )}
                  </div>
                </div>
              </Collapse>
            </div>
          );
        })}
      </div>
    </>
  );
};
export default Awardee;
